import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import { Pencil } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_STATUSES = ["pending", "in_production", "qc", "dispatched", "cancelled"];

/**
 * Edit Order Info dialog — surfaces every free-form field the Overview panel
 * displays so operators can amend an order (PO, dates, confirmer, delivery
 * details, remarks) without re-opening the full Master Job Card form.
 *
 * PATCHes the job in place and calls the parent's `onSaved` to refresh.
 */
export default function EditOrderInfoDialog({ job, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState(FALLBACK_STATUSES);

  // Pull the authoritative status list from the backend so pipeline statuses
  // (Converted BPR, part_qty_avt, …) all show up and the Status select never
  // renders blank on a job that uses one of them.
  useEffect(() => {
    let cancelled = false;
    api.get("/jobs/statuses")
      .then(({ data }) => { if (!cancelled && Array.isArray(data) && data.length) setStatuses(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const start = () => {
    setF({
      product_code: job.product_code || "",
      old_code: job.old_code || "",
      sap_code: job.sap_code || "",
      po_number: job.po_number || "",
      po_date: job.po_date || "",
      order_confirmed_by: job.order_confirmed_by || "",
      date_received: job.date_received || "",
      bpr_issue_date: job.bpr_issue_date || "",
      due_date: job.due_date || "",
      quantity: job.quantity ?? "",
      unit: job.unit || "NOS",
      job_type: job.job_type || "New Job",
      status: job.status || "pending",
      delivery_location: job.delivery_location || "",
      delivery_area: job.delivery_area || "",
      notes: job.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f };
      // Strip blanks so PATCH doesn't wipe values with empty strings
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) delete payload[k];
      });
      if (payload.quantity !== undefined) payload.quantity = Number(payload.quantity);
      await api.patch(`/jobs/${job.id}`, payload);
      toast.success("Order Info updated");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? start() : setOpen(false))}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="edit-order-info-btn" className="rounded-sm h-8 gap-1.5">
          <Pencil size={12} /> Edit Order Info
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit Order Info · {job.mpwo_number || job.job_number}</DialogTitle>
          <DialogDescription>Amend PO details, dates, delivery info and status. Master Job Card spec fields are edited from the MJC tab.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <F label="Product Code" testid="eoi-product_code" value={f.product_code} onChange={(v) => setF({ ...f, product_code: v })} />
          <F label="Old Code" testid="eoi-old_code" value={f.old_code} onChange={(v) => setF({ ...f, old_code: v })} />
          <F label="SAP Code" testid="eoi-sap_code" value={f.sap_code} onChange={(v) => setF({ ...f, sap_code: v })} />
          <F label="PO Number" testid="eoi-po_number" value={f.po_number} onChange={(v) => setF({ ...f, po_number: v })} />
          <D label="PO Date" testid="eoi-po_date" value={f.po_date} onChange={(v) => setF({ ...f, po_date: v })} />
          <F label="Order Confirmed By" testid="eoi-confirmed_by" value={f.order_confirmed_by} onChange={(v) => setF({ ...f, order_confirmed_by: v })} />
          <D label="Date Received" testid="eoi-date_received" value={f.date_received} onChange={(v) => setF({ ...f, date_received: v })} />
          <D label="BPR Issue Date" testid="eoi-bpr_issue_date" value={f.bpr_issue_date} onChange={(v) => setF({ ...f, bpr_issue_date: v })} />
          <D label="Job Due Date" testid="eoi-due_date" value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} />
          <F label="Quantity" testid="eoi-quantity" type="number" value={f.quantity} onChange={(v) => setF({ ...f, quantity: v })} />
          <F label="Unit" testid="eoi-unit" value={f.unit} onChange={(v) => setF({ ...f, unit: v })} />
          <div>
            <label className="label-caps block mb-1 text-[10px]">Job Type</label>
            <Select value={f.job_type} onValueChange={(v) => setF({ ...f, job_type: v })}>
              <SelectTrigger data-testid="eoi-job_type" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="New Job">New Job</SelectItem>
                <SelectItem value="Repeat Job">Repeat Job</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-caps block mb-1 text-[10px]">Status</label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="eoi-status" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {statuses.map((s) => (
                  <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <F label="Delivery Area" testid="eoi-delivery_area" value={f.delivery_area} onChange={(v) => setF({ ...f, delivery_area: v })} />
          <div className="col-span-full">
            <label className="label-caps block mb-1 text-[10px]">Delivery Location</label>
            <Textarea data-testid="eoi-delivery_location" value={f.delivery_location} onChange={(e) => setF({ ...f, delivery_location: e.target.value })} rows={2} className="rounded-sm" />
          </div>
          <div className="col-span-full">
            <label className="label-caps block mb-1 text-[10px]">Notes / Remarks</label>
            <Textarea data-testid="eoi-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="rounded-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="eoi-cancel">Cancel</Button>
          <Button disabled={saving} onClick={save} data-testid="eoi-save" className="bg-[#FF5A00] hover:bg-[#e04f00]">{saving ? "Saving…" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="label-caps block mb-1 text-[10px]">{label}</label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm h-9 text-sm" />
    </div>
  );
}

function D({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-caps block mb-1 text-[10px]">{label}</label>
      <DatePicker value={value} onChange={onChange} testId={testid} />
    </div>
  );
}
